import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings, Plus, DollarSign, Eye, EyeOff, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TestingCenterSettings() {
  const [pageSettings, setPageSettings] = useState({
    title: 'Testing Center - Board Prep',
    description: 'Prepare for your acupuncture board exams',
    is_visible: true
  });
  
  const [tiers, setTiers] = useState([
    { id: 'free', name: 'Free', price: 0, description: 'Basic practice questions', features: ['Limited questions', 'Basic categories'] },
    { id: 'comps_ready', name: 'Comps Ready', price: 29.99, description: 'Comprehensive exam prep', features: ['All categories', 'Timed tests', 'Progress tracking'] },
    { id: 'advanced', name: 'Advanced', price: 49.99, description: 'Advanced practice', features: ['Expert-level questions', 'Detailed explanations', 'Performance analytics'] }
  ]);

  const [showTestForm, setShowTestForm] = useState(false);
  const [testForm, setTestForm] = useState({
    title: '',
    description: '',
    tier: 'basic',
    category: 'acupuncture',
    question_count: 10,
    passing_score: 70
  });

  const handleSavePageSettings = async () => {
    toast.success('Page settings saved (feature coming soon)');
  };

  const handleCreateTest = async (e) => {
    e.preventDefault();
    
    const time_limit_seconds = ['comps_ready', 'advanced'].includes(testForm.tier)
      ? Math.round(testForm.question_count * 1.25)
      : null;

    const testData = {
      ...testForm,
      time_limit_seconds
    };

    try {
      await axios.post(`${API}/admin/tests`, testData, {
        withCredentials: true
      });
      toast.success('Test created! Go to Test Bank to add questions.');
      setShowTestForm(false);
      setTestForm({ title: '', description: '', tier: 'basic', category: 'acupuncture', question_count: 10, passing_score: 70 });
    } catch (error) {
      toast.error('Failed to create test');
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl retro-title text-[#2C1810]">Testing Center Settings</h2>
        <button
          onClick={() => setShowTestForm(true)}
          className="retro-button flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create New Test
        </button>
      </div>

      {/* Page Settings */}
      <div className="cassette-card p-6">
        <div className="flex items-center gap-3 mb-6">
          <Settings className="w-6 h-6 text-[#E8934D]" />
          <h3 className="text-2xl retro-title text-[#2C1810]">Page Configuration</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-[#6B4423] mb-2">Page Title</label>
            <input
              type="text"
              value={pageSettings.title}
              onChange={(e) => setPageSettings({ ...pageSettings, title: e.target.value })}
              className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#6B4423] mb-2">Page Description</label>
            <textarea
              value={pageSettings.description}
              onChange={(e) => setPageSettings({ ...pageSettings, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={pageSettings.is_visible}
              onChange={(e) => setPageSettings({ ...pageSettings, is_visible: e.target.checked })}
              className="w-5 h-5"
            />
            <label className="text-sm font-semibold text-[#6B4423]">
              {pageSettings.is_visible ? <Eye className="w-5 h-5 inline mr-2" /> : <EyeOff className="w-5 h-5 inline mr-2" />}
              Page Visible to Students
            </label>
          </div>

          <button
            onClick={handleSavePageSettings}
            className="retro-button w-full sm:w-auto"
          >
            Save Page Settings
          </button>
        </div>
      </div>

      {/* Subscription Tiers */}
      <div className="cassette-card p-6">
        <div className="flex items-center gap-3 mb-6">
          <DollarSign className="w-6 h-6 text-[#E8934D]" />
          <h3 className="text-2xl retro-title text-[#2C1810]">Subscription Tiers</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier) => (
            <div key={tier.id} className="border-2 border-[#E8934D] rounded-lg p-4 bg-[#F4E4C1]">
              <h4 className="text-xl retro-title text-[#2C1810] mb-2">{tier.name}</h4>
              <p className="text-2xl font-bold text-[#E8934D] mb-2">
                ${tier.price.toFixed(2)}/month
              </p>
              <p className="text-sm text-[#6B4423] mb-4">{tier.description}</p>
              <ul className="space-y-2">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="text-sm text-[#2C1810] flex items-start">
                    <span className="text-[#E8934D] mr-2">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-sm text-[#6B4423] mt-4">
          Note: Tier pricing and features are display-only for now. PayPal integration manages actual payments.
        </p>
      </div>

      {/* Create Test Modal */}
      {showTestForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="cassette-card p-6 max-w-2xl w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl retro-title text-[#2C1810]">Create New Test</h3>
              <button onClick={() => setShowTestForm(false)} className="text-[#2C1810]">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreateTest} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Test Title</label>
                <input
                  type="text"
                  value={testForm.title}
                  onChange={(e) => setTestForm({ ...testForm, title: e.target.value })}
                  required
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Description</label>
                <textarea
                  value={testForm.description}
                  onChange={(e) => setTestForm({ ...testForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Category</label>
                  <select
                    value={testForm.category}
                    onChange={(e) => setTestForm({ ...testForm, category: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  >
                    <option value="acupuncture">Acupuncture</option>
                    <option value="biomedicine">Biomedicine</option>
                    <option value="foundations">Foundations</option>
                    <option value="herbs">Herbs</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Tier</label>
                  <select
                    value={testForm.tier}
                    onChange={(e) => setTestForm({ ...testForm, tier: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  >
                    <option value="basic">Free</option>
                    <option value="comps_ready">Comps Ready</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Question Count</label>
                  <input
                    type="number"
                    value={testForm.question_count}
                    onChange={(e) => setTestForm({ ...testForm, question_count: parseInt(e.target.value) })}
                    min="1"
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Passing Score (%)</label>
                  <input
                    type="number"
                    value={testForm.passing_score}
                    onChange={(e) => setTestForm({ ...testForm, passing_score: parseInt(e.target.value) })}
                    min="0"
                    max="100"
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button type="submit" className="retro-button flex-1">
                  Create Test
                </button>
                <button
                  type="button"
                  onClick={() => setShowTestForm(false)}
                  className="retro-button-small flex-1 bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
